import React, { useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail, waLink } from "@/lib/api";
import FormSuccess from "@/components/FormSuccess";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageCircle, UploadCloud, X, FileText, ShieldCheck, Clock } from "lucide-react";
import { useContent } from "@/lib/useContent";
import { usePageMeta, useJsonLd } from "@/lib/seo";
import { trackEvent } from "@/lib/analytics";

const VISA_TYPES = [
  "30 Days Tourist Visa",
  "60 Days Tourist Visa",
  "Multiple Entry Visa",
  "Residence Visa",
  "Airport to Airport Visa Change",
  "Visa Extension",
  "Group Visa Application",
  "Corporate Visa Services",
  "Other",
];

const DOC_FIELDS = [
  { key: "passport_copy", label: "Passport Copy" },
  { key: "passport_photo", label: "Passport Photograph" },
  { key: "current_visa", label: "Current Visa" },
  { key: "flight_reservation", label: "Flight Reservation" },
  { key: "hotel_reservation", label: "Hotel Reservation" },
  { key: "travel_insurance", label: "Travel Insurance" },
  { key: "invitation_letter", label: "Invitation Letter" },
  { key: "sponsor_documents", label: "Sponsor Documents" },
  { key: "other_documents", label: "Other Supporting Documents" },
];

const ACCEPT = ".pdf,.jpg,.jpeg,.png";
const MAX_MB = 10;

function UploadZone({ label, files, onFiles, testId }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const addFiles = (list) => {
    const valid = [];
    for (const f of Array.from(list)) {
      const ext = f.name.split(".").pop().toLowerCase();
      if (!["pdf", "jpg", "jpeg", "png"].includes(ext)) { toast.error(`${f.name}: only PDF, JPG, JPEG or PNG accepted`); continue; }
      if (f.size > MAX_MB * 1024 * 1024) { toast.error(`${f.name}: exceeds the ${MAX_MB}MB limit`); continue; }
      valid.push(f);
    }
    if (valid.length) onFiles([...files, ...valid]);
  };
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-[#4B5563] font-semibold">{label}</Label>
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
        className={`mt-1.5 rounded-xl border-2 border-dashed px-4 py-5 text-center cursor-pointer transition-colors ${drag ? "border-[#D4A62A] bg-[#FDF8EC]" : "border-[#E5E7EB] hover:border-[#D4A62A] bg-[#F9FAFB]"}`}
        data-testid={testId}>
        <UploadCloud size={20} className="mx-auto text-[#D4A62A]" />
        <p className="text-xs text-[#4B5563] mt-1.5">Drag &amp; drop or <span className="text-[#0A2E4D] font-semibold underline">browse</span></p>
        <input ref={inputRef} type="file" multiple accept={ACCEPT} className="hidden"
          onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
      </div>
      {files.length > 0 && (
        <ul className="mt-2 space-y-1.5">
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`} className="flex items-center gap-2 text-xs text-[#0A2E4D] bg-white border border-[#E5E7EB] rounded-lg px-3 py-2">
              <FileText size={13} className="text-[#D4A62A] shrink-0" />
              <span className="flex-1 truncate">{f.name}</span>
              <span className="text-[#9CA3AF]">{(f.size / 1024 / 1024).toFixed(1)}MB</span>
              <button type="button" onClick={() => onFiles(files.filter((_, j) => j !== i))} aria-label="Remove file" className="text-[#9CA3AF] hover:text-red-500"><X size={13} /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function VisaForm() {
  usePageMeta("UAE Visa Application", "Apply for your UAE visa online — upload documents securely and let our consultants handle submission, tracking and updates.");
  const uaeVisaTypes = useContent("uae_visa_types");
  const serviceSchema = React.useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://makchester.com/visa#service",
    name: "UAE Visa Assistance",
    serviceType: "UAE tourist visa application assistance",
    url: "https://makchester.com/visa",
    description: "Makchester Tours & Travel provides assistance with UAE tourist visa applications, including available short-term visa options and related travel support.",
    provider: { "@id": "https://makchester.com/#travelagency" },
    areaServed: [
      { "@type": "Country", name: "United Arab Emirates" },
      { "@type": "Country", name: "Zimbabwe" },
      { "@type": "Country", name: "South Africa" },
      "Worldwide",
    ],
    availableChannel: {
      "@type": "ServiceChannel",
      serviceUrl: "https://makchester.com/visa",
      servicePhone: { "@type": "ContactPoint", telephone: "+971559209503", contactType: "customer service" },
    },
    offers: (uaeVisaTypes || []).filter((t) => t.available && t.name).map((t) => ({
      "@type": "Offer",
      name: `UAE ${t.name} Assistance`,
      itemOffered: { "@type": "Service", name: `UAE ${t.name} Assistance`, provider: { "@id": "https://makchester.com/#travelagency" } },
    })),
  }), [uaeVisaTypes]);
  useJsonLd("ld-service", serviceSchema);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [form, setForm] = useState({
    full_name: "", phone: "", whatsapp: "", email: "", nationality: "",
    current_location: "", current_visa_status: "", visa_type: VISA_TYPES[0],
    travel_date: "", num_applicants: 1, notes: "",
  });
  const [files, setFiles] = useState(Object.fromEntries(DOC_FIELDS.map((d) => [d.key, []])));

  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setProgress(0);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, String(v ?? "")));
      Object.entries(files).forEach(([k, list]) => list.forEach((f) => fd.append(k, f)));
      await api.post("/public/visa", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (ev) => setProgress(ev.total ? Math.round((ev.loaded / ev.total) * 100) : 0),
      });
      trackEvent("enquiry_submitted", { form: "visa" });
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setDone(false);
    setProgress(0);
    setForm({ full_name: "", phone: "", whatsapp: "", email: "", nationality: "", current_location: "", current_visa_status: "", visa_type: VISA_TYPES[0], travel_date: "", num_applicants: 1, notes: "" });
    setFiles(Object.fromEntries(DOC_FIELDS.map((d) => [d.key, []])));
  };

  return (
    <div className="bg-white">
      <section className="bg-[#0A2E4D] text-white py-16">
        <div className="max-w-6xl mx-auto px-6">
          <span className="mk-gold-line mb-6 block" />
          <p className="text-xs uppercase tracking-[0.25em] text-[#D4A62A] font-bold mb-3">UAE Visa Services</p>
          <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl" data-testid="visa-page-heading">Start Your UAE Visa Application</h1>
          <p className="text-slate-300 mt-4 max-w-xl">Upload your documents securely — our consultants review, submit and track your application, keeping you updated on WhatsApp.</p>
        </div>
      </section>

      {/* UAE visa types */}
      <section className="py-16 bg-[#F9FAFB]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {(uaeVisaTypes || []).map((t) => (
              <div key={t.name} className={`mk-card p-6 flex flex-col ${!t.available ? "opacity-80" : ""}`} data-testid={`visa-type-card-${t.name.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-serif-display text-base text-[#0A2E4D] leading-snug">{t.name}</h3>
                  {!t.available && <span className="shrink-0 text-[9px] font-bold uppercase tracking-wider bg-[#F3F4F6] text-[#4B5563] rounded-full px-2 py-0.5">Coming Soon</span>}
                </div>
                <p className="text-xs text-[#4B5563] mt-2 flex-1 leading-relaxed">{t.desc}</p>
                {t.available && t.price && <div className="mt-3 text-sm font-bold text-[#D4A62A]" data-testid={`visa-type-price-${t.name.toLowerCase().replace(/\s+/g, "-")}`}>{t.price}</div>}
                {t.available && (
                  <a href={waLink(`Hello Makchester Tours & Travel, I would like assistance with a ${t.name}.`)} target="_blank" rel="noreferrer"
                     className="mt-4 inline-flex items-center justify-center gap-1.5 text-xs bg-[#25D366] hover:bg-[#1ebe57] text-white py-2.5 rounded-full transition-colors font-medium">
                    <MessageCircle size={12} /> Enquire on WhatsApp
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-3xl mx-auto px-6">
          <div className="mb-8 text-center">
            <span className="mk-gold-line mb-4 inline-block" />
            <h2 className="font-serif-display text-2xl sm:text-3xl text-[#0A2E4D]">Submit your application online</h2>
            <p className="text-sm text-[#4B5563] mt-2">Accepted formats: PDF, JPG, JPEG, PNG · Maximum 10MB per file · Multiple files allowed per document.</p>
          </div>
          {done ? (
            <FormSuccess
              waMessage="Hello Makchester Tours & Travel, I have just submitted my UAE visa application online."
              onReset={reset}
              testId="visa-form-success"
            />
          ) : (
            <form onSubmit={submit} className="bg-white border border-[#E5E7EB] rounded-2xl p-8 space-y-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="visa-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Full name" required><Input data-testid="visa-full-name" value={form.full_name} onChange={set("full_name")} required /></Field>
                <Field label="Phone number" required><Input data-testid="visa-phone" value={form.phone} onChange={set("phone")} required /></Field>
                <Field label="WhatsApp number"><Input data-testid="visa-whatsapp" value={form.whatsapp} onChange={set("whatsapp")} /></Field>
                <Field label="Email"><Input type="email" data-testid="visa-email" value={form.email} onChange={set("email")} /></Field>
                <Field label="Nationality" required><Input data-testid="visa-nationality" value={form.nationality} onChange={set("nationality")} required /></Field>
                <Field label="Current location"><Input data-testid="visa-current-location" value={form.current_location} onChange={set("current_location")} /></Field>
                <Field label="Current visa status"><Input placeholder="e.g. Visit visa, Resident, None" data-testid="visa-current-status" value={form.current_visa_status} onChange={set("current_visa_status")} /></Field>
                <Field label="Visa type requested" required>
                  <Select value={form.visa_type} onValueChange={(v) => setForm({ ...form, visa_type: v })}>
                    <SelectTrigger data-testid="visa-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {VISA_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Travel date"><Input type="date" data-testid="visa-travel-date" value={form.travel_date} onChange={set("travel_date")} /></Field>
                <Field label="Number of applicants"><Input type="number" min={1} data-testid="visa-num-applicants" value={form.num_applicants} onChange={set("num_applicants")} /></Field>
              </div>

              <div>
                <h3 className="font-serif-display text-lg text-[#0A2E4D] mb-1">Upload Documents</h3>
                <p className="text-xs text-[#4B5563] mb-4">Only your passport copy and photograph are essential to start — add the rest if you have them.</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {DOC_FIELDS.map((d) => (
                    <UploadZone key={d.key} label={d.label} files={files[d.key]}
                      onFiles={(list) => setFiles({ ...files, [d.key]: list })}
                      testId={`visa-upload-${d.key.replace(/_/g, "-")}`} />
                  ))}
                </div>
              </div>

              <Field label="Notes"><Textarea rows={4} data-testid="visa-notes" value={form.notes} onChange={set("notes")} /></Field>

              <div className="flex items-center gap-2.5 text-xs text-[#4B5563] bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl px-4 py-3" data-testid="visa-secure-message">
                <ShieldCheck size={16} className="text-[#D4A62A] shrink-0" />
                Your documents are transmitted over an encrypted connection and stored securely. Only authorised Makchester consultants can access them.
              </div>

              {busy && (
                <div data-testid="visa-upload-progress">
                  <div className="flex justify-between text-xs text-[#4B5563] mb-1.5"><span className="flex items-center gap-1.5"><Clock size={12} /> Uploading documents…</span><span>{progress}%</span></div>
                  <div className="h-2 rounded-full bg-[#F3F4F6] overflow-hidden"><div className="h-full bg-[#D4A62A] transition-all duration-300" style={{ width: `${progress}%` }} /></div>
                </div>
              )}

              <button type="submit" disabled={busy}
                      className="w-full mk-btn-navy !py-4 disabled:opacity-60"
                      data-testid="visa-submit-btn">
                {busy ? "Submitting…" : "Submit Visa Application"}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-[#4B5563] font-semibold">{label}{required && <span className="text-[#EF4444] ml-1">*</span>}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
